import {
  Box,
  Button,
  Heading,
  HStack,
  MenuButton,
  Stack,
  VStack,
} from "@chakra-ui/react";
import {
  Card,
  CardHeader,
  ExpandableItem,
  LabeledItem,
  LoadingOverlay,
  ModalConfirm,
} from "@components/ui-kit";
import { NotFoundBrand } from "@components/widgets/ErrorPage";
import { Trans, t } from "@lingui/macro";
import {
  useBrandDetailQuery,
  useUpdateBrandMutation,
} from "@src/__generated__/graphql";
import { CardContactPersons } from "@src/components/modules/brands/detail/CardContactPersons";
import { CardHourlyRates } from "@src/components/modules/brands/detail/CardHourlyRates";
import { PopoverBrandActions } from "@src/components/modules/brands/detail/PopoverBrandActions";
import { PageLoading } from "@src/components/modules/brands/page-loading";
import { GoogleDriveLink } from "@src/components/ui-kit/GoogleDriveLink";
import { Icon } from "@src/components/ui-kit/Icon";
import { SharepointLink } from "@src/components/ui-kit/SharepointLink";
import { NavigationBrand } from "@src/components/widgets/AppSubNav/NavigationBrand";
import { onError } from "@src/utils/apollo";
import { can, cannot, withCan } from "@src/utils/components/permissions";
import { headTitle } from "@src/utils/head-title";
import { useStore } from "@src/utils/hooks";
import { getQueryParam } from "@src/utils/routing";
import { UserSelect } from "@src/widgets/UserSelect";
import { observer, useLocalObservable } from "mobx-react-lite";
import Head from "next/head";
import router, { useRouter } from "next/router";
import React from "react";
import { Store } from "./Store";

export const PageBrandDetailCtx = React.createContext<Store>(
  null as any as Store,
);

export const PageBrandDetail = withCan(
  { do: "brand_read" },
  observer(function PageBrandDetail() {
    const appStore = useStore();
    const { query } = useRouter();
    const brandId = getQueryParam("id", query);
    const { store } = useLocalObservable(() => ({
      store: new Store(appStore),
    }));

    const [update] = useUpdateBrandMutation({ ...onError() });

    const onSelectResponsiblePerson = (val: string) => {
      store.form.responsible_person_id.onChange(val);
      update({
        variables: {
          input: {
            name: brand.name,
            code: brand.code,
            numeric_code: brand.numeric_code,
            responsible_person_user_id: val,
            id: brand.id,
            default_billing_entity_id: brand.defaultBillingEntity?.id,
          },
        },
      });
    };

    const onSelectOtherResponsiblePersons = (val: string[]) => {
      store.form.other_responsible_persons_ids.onChange(val);
      update({
        variables: {
          input: {
            name: brand.name,
            code: brand.code,
            numeric_code: brand.numeric_code,
            other_responsible_person_user_ids: val,
            id: brand.id,
            default_billing_entity_id: brand.defaultBillingEntity?.id,
          },
        },
      });
    };

    const { data, refetch, loading } = useBrandDetailQuery({
      variables: { id: brandId },
      skip: !brandId,
      notifyOnNetworkStatusChange: true,
      onCompleted(data) {
        store.fromBrandDetailQuery(data);
      },
    });

    store.refetch = refetch;

    if (loading) {
      return <PageLoading brandId={brandId} />;
    }

    if (!data?.brand) {
      return <NotFoundBrand />;
    }

    if (!store.brand) {
      return null;
    }

    const { brand } = store;
    const pipedriveCustomFieldValues = brand.pipedriveCustomFieldValues.filter(
      (item) => !!item.customField.label && !!item.value,
    );

    return (
      <PageBrandDetailCtx.Provider value={store}>
        <Head>
          <title>{headTitle(brand.name)}</title>
        </Head>
        <Card>
          <HStack
            justifyContent="space-between"
            px="4"
            borderBottom="1px"
            borderBottomColor="gray.200"
          >
            <NavigationBrand id={brandId} variant="page" />

            <HStack spacing="2">
              {brand.cloudUrl && (
                <SharepointLink href={brand.cloudUrl} mr="2" />
              )}
              {brand.driveCloudUrl && (
                <GoogleDriveLink href={brand.driveCloudUrl} mr="2" />
              )}
              {(can("brand_update") || can("brand_delete")) && (
                <PopoverBrandActions
                  client={brand.client}
                  brand={store.brandForModal}
                  onAfterUpdate={() => store.refetch?.()}
                  onAfterDelete={() => router.back()}
                  hideClientLink={false}
                  trigger={
                    <MenuButton
                      as={Button}
                      colorScheme="grey"
                      rightIcon={<Icon name="chevron-down" />}
                      variant="outline"
                    >
                      <Trans>Actions</Trans>
                    </MenuButton>
                  }
                />
              )}
            </HStack>
          </HStack>

          <LoadingOverlay transparent={false} minH="50dvh" isLoading={loading}>
            <Stack
              flexDir={{
                base: "column",
                md: "row",
              }}
              w="full"
              spacing={{
                base: "4",
                md: "0",
              }}
            >
              <VStack
                w="full"
                borderRight={{
                  base: "none",
                  md: "1px solid",
                }}
                borderRightColor={{
                  base: "none",
                  md: "gray.200",
                }}
                spacing="4"
              >
                <CardContactPersons
                  brand={store.brand}
                  contacts={store.contacts}
                  onAddContact={store.addContact}
                  onUpdateContact={store.updateContact}
                  onRemoveContact={store.removeContact}
                />

                <Box
                  w="full"
                  borderTop="1px"
                  borderTopColor="gray.200"
                  borderBottom="1px"
                  borderBottomColor="gray.200"
                >
                  <CardHeader
                    action={
                      <Button
                        colorScheme="grey"
                        leftIcon={<Icon name="plus" />}
                        onClick={() => store.handleAddClientAccess(brandId)}
                        variant="outline"
                      >
                        <Trans>Add client access</Trans>
                      </Button>
                    }
                    icon="user-02"
                    title={
                      <Heading size="md">
                        <Trans>Client access</Trans>
                      </Heading>
                    }
                  />

                  <Card.Section>
                    {store.brand.users.map(
                      (scopedUser) =>
                        scopedUser.scope?.value && (
                          <ExpandableItem
                            key={scopedUser.id}
                            title={`${scopedUser.full_name} (${scopedUser.email})`}
                            popoverActions={[
                              {
                                iconName: "edit-02",
                                content: t`Edit`,
                                onAction: () => {
                                  if (!scopedUser.scope?.value) return;
                                  store.handleScopedUserEdit(
                                    brandId,
                                    scopedUser.id,
                                    scopedUser.scope.value,
                                  );
                                },
                              },
                              {
                                iconName: "trash-03",
                                content: t`Delete`,
                                onAction: () =>
                                  store.handleScopedUserDelete(scopedUser.id),
                              },
                            ]}
                          >
                            <LabeledItem label={t`First name`}>
                              {scopedUser.first_name}
                            </LabeledItem>
                            <LabeledItem label={t`Last name`}>
                              {scopedUser.last_name}
                            </LabeledItem>
                            <LabeledItem label={t`Email`}>
                              {scopedUser.email}
                            </LabeledItem>
                            <LabeledItem label={t`Access`}>
                              {store.getScopedUserScopeAccessLabel(
                                scopedUser.scope_access,
                              )}
                            </LabeledItem>
                          </ExpandableItem>
                        ),
                    )}
                  </Card.Section>
                </Box>

                <CardHourlyRates />
              </VStack>

              <VStack w="full" spacing="4">
                <Box w="full" borderBottom="1px" borderBottomColor="gray.200">
                  <CardHeader
                    icon="user-02"
                    title={
                      <Heading size="md">
                        <Trans>Responsible manager</Trans>
                      </Heading>
                    }
                  />

                  <Card.Section>
                    <UserSelect
                      isDisabled={cannot(
                        "project_create_all",
                        "brand_updateManagers",
                      )}
                      placeholder={t`Select project manager`}
                      label={""}
                      onChange={onSelectResponsiblePerson}
                      value={store.form.responsible_person_id.$}
                    />
                  </Card.Section>
                </Box>

                <Box w="full">
                  <CardHeader
                    icon="user-02"
                    title={
                      <Heading size="md">
                        <Trans>Other managers</Trans>
                      </Heading>
                    }
                  />

                  <Card.Section>
                    <UserSelect
                      isMulti
                      placeholder={t`Select project manager`}
                      isDisabled={cannot("brand_updateManagers")}
                      label={""}
                      onChange={onSelectOtherResponsiblePersons}
                      value={store.form.other_responsible_persons_ids.$}
                    />
                  </Card.Section>
                </Box>
                {pipedriveCustomFieldValues.length > 0 && (
                  <Card w="full">
                    <CardHeader icon="bank" title={t`Custom fields`} />
                    <Card.Section>
                      {pipedriveCustomFieldValues.map((item) => (
                        <LabeledItem
                          key={`${item.customField.label}-${item.value}`}
                          label={item.customField.label ?? ""}
                        >
                          {item.value}
                        </LabeledItem>
                      ))}
                    </Card.Section>
                  </Card>
                )}
              </VStack>
            </Stack>

            <ModalConfirm
              destructive
              confirmBtnTitle={t`Delete`}
              heading={t`Delete user ${store.scopedUserToDelete?.full_name}`}
              message={t`Do you really want to permanently delete ${store.scopedUserToDelete?.full_name}?`}
              isOpen={store.isScopedUserDeleteModalOpen}
              onClose={store.handleScopedUserDeleteCancel}
              onConfirm={store.handleScopedUserDeleteConfirm}
              isLoading={store.deleteScopedUserMutator.isLoading.value}
            />
          </LoadingOverlay>
        </Card>
      </PageBrandDetailCtx.Provider>
    );
  }),
);
